import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Notice } from './ui.js';
import { IconClose } from './icons.js';

/*
 * Čítanie QR kódu z kamery.
 *
 * Dve cesty, pretože jedna nestačí:
 *
 * - `BarcodeDetector` je vstavaný v prehliadači a nestojí nič. Chrome
 *   na Androide aj na počítači ho má, Safari na iPhone **nie**.
 * - Keď chýba, dotiahne sa `jsqr`. Načíta sa až v tej chvíli
 *   (`import()`), takže kto ho nepotrebuje, ten ho ani nestiahne.
 *
 * Kamera funguje LEN v zabezpečenom kontexte — HTTPS alebo `localhost`.
 * Na `http://` s IP adresou ju prehliadač odmietne bez ohľadu na kód,
 * preto sa to kontroluje dopredu a povie sa to zrozumiteľne.
 */

/** Ako často sa pozerá na obraz; častejšie by len hrialo procesor. */
const SNIMKA_MS = 250;

/**
 * Typ vstavaného čítača.
 *
 * `BarcodeDetector` nie je v `lib.dom.d.ts`, takže sa opisuje tu —
 * a len to, čo sa z neho používa.
 */
interface CitacKodov {
  detect(obraz: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

interface CitacKodovKonstruktor {
  new (nastavenia: { formats: string[] }): CitacKodov;
  getSupportedFormats?: () => Promise<string[]>;
}

const vstavanyCitac = (): CitacKodovKonstruktor | undefined => {
  const kandidat = (globalThis as { BarcodeDetector?: CitacKodovKonstruktor }).BarcodeDetector;
  return typeof kandidat === 'function' ? kandidat : undefined;
};

/** Prečíta kód z obrazu; `undefined` znamená, že na snímke žiadny nie je. */
type Precitaj = (platno: HTMLCanvasElement) => Promise<string | undefined>;

async function pripravCitanie(): Promise<Precitaj> {
  const Vstavany = vstavanyCitac();
  if (Vstavany !== undefined) {
    const citac = new Vstavany({ formats: ['qr_code'] });
    return async (platno) => (await citac.detect(platno))[0]?.rawValue;
  }

  // Safari na iPhone vstavaný čítač nemá; vtedy a len vtedy sa doťahuje.
  const { default: jsQR } = await import('jsqr');
  return (platno) => {
    const kontext = platno.getContext('2d');
    if (kontext === null) return Promise.resolve(undefined);
    const obraz = kontext.getImageData(0, 0, platno.width, platno.height);
    return Promise.resolve(jsQR(obraz.data, obraz.width, obraz.height)?.data);
  };
}

/**
 * Je kamera v tomto kontexte vôbec dostupná?
 *
 * Rozlišuje sa zámerne od „nepovolil si ju": nezabezpečené spojenie sa
 * povolením nedá obísť a rada „povoľ kameru" by bola klamstvo.
 */
export function kameraDostupna(): boolean {
  return globalThis.isSecureContext && navigator.mediaDevices !== undefined;
}

export function SkenerQr({
  naPrecitanie,
  naZavretie,
}: {
  readonly naPrecitanie: (hodnota: string) => void;
  readonly naZavretie: () => void;
}): ReactNode {
  const video = useRef<HTMLVideoElement | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [hlada, setHlada] = useState(true);
  const hotovo = useRef(false);

  const zavri = useCallback(() => {
    hotovo.current = true;
    naZavretie();
  }, [naZavretie]);

  useEffect(() => {
    if (!kameraDostupna()) {
      setChyba(
        'Prehliadač kameru nepustí, pretože spojenie nie je zabezpečené. Otvor LoxLink cez https://.',
      );
      setHlada(false);
      return;
    }

    let prud: MediaStream | undefined;
    let casovac: ReturnType<typeof setTimeout> | undefined;
    hotovo.current = false;

    const platno = document.createElement('canvas');

    const spusti = async (): Promise<void> => {
      try {
        // Zadná kamera; `ideal` a nie `exact`, aby to nespadlo na počítači.
        prud = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        const prvok = video.current;
        if (prvok === null || hotovo.current) return;
        prvok.srcObject = prud;
        await prvok.play();

        const precitaj = await pripravCitanie();

        const krok = async (): Promise<void> => {
          if (hotovo.current) return;
          const sirka = prvok.videoWidth;
          const vyska = prvok.videoHeight;
          if (sirka > 0 && vyska > 0) {
            platno.width = sirka;
            platno.height = vyska;
            platno.getContext('2d')?.drawImage(prvok, 0, 0, sirka, vyska);
            const hodnota = await precitaj(platno);
            if (hodnota !== undefined && hodnota.length > 0) {
              hotovo.current = true;
              naPrecitanie(hodnota);
              return;
            }
          }
          casovac = setTimeout(() => void krok(), SNIMKA_MS);
        };
        await krok();
      } catch {
        /*
         * Najčastejšie je to odmietnutie kamery; technický detail by
         * tu nikomu nepomohol.
         */
        if (!hotovo.current) {
          setChyba('Ku kamere sa nedá dostať. Skontroluj, či si jej prístup povolil.');
          setHlada(false);
        }
      }
    };

    void spusti();

    return () => {
      hotovo.current = true;
      if (casovac !== undefined) clearTimeout(casovac);
      // Bez tohto zostane kamera svietiť aj po zatvorení.
      prud?.getTracks().forEach((t) => {
        t.stop();
      });
    };
  }, [naPrecitanie]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-(--color-surface)"
      role="dialog"
      aria-modal="true"
      aria-label="Skenovanie QR kódu"
    >
      <div className="flex items-center justify-between gap-3 border-b border-(--color-border) p-4">
        <p className="text-sm font-medium text-(--color-text)">Nasmeruj kameru na QR kód</p>
        <Button variant="ghost" onClick={zavri} aria-label="Zavrieť skenovanie">
          <IconClose />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-4">
        {chyba !== null ? (
          <Notice tone="danger">
            <p>{chyba}</p>
          </Notice>
        ) : (
          <>
            {/* Vlastný obraz kamery; nie je to informácia, len hľadáčik. */}
            <video
              ref={video}
              className="max-h-full w-full max-w-md rounded-lg bg-(--color-surface-sunken)"
              playsInline
              muted
            />
            {hlada && (
              <p className="text-sm text-(--color-text-muted)">
                Kód je na krabici alebo na displeji zariadenia.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
